import { jitsiLocalStorage } from '@jitsi/js-utils';

import { ReducerRegistry } from '../base/redux';
import { _TRANSCRIBER_LEFT } from '../transcribing/actionTypes';

import {
    CLOSE_LANGUAGE_SELECTOR_MODAL,
    OPEN_LANGUAGE_SELECTOR_MODAL,
    REMOVE_CACHED_TRANSCRIPT_MESSAGE,
    REMOVE_TRANSCRIPT_MESSAGE,
    SET_CAPTIONS_VIEW,
    SET_DEFAULT_TRANSCRIPTION_LANGUAGE,
    SET_REQUESTING_SUBTITLES,
    SET_TRANSLATED_AUDIO_TRACK,
    SET_TRANSLATED_VOICE_ENABLED,
    SET_VOICE_CAPABILITY,
    SET_VOICE_CATALOG,
    SET_VOICE_MODE_STATE,
    TOGGLE_REQUESTING_CAPTIONS,
    TOGGLE_REQUESTING_SUBTITLES,
    UPDATE_TRANSCRIPT_MESSAGE
} from './actionTypes';

/**
 * Determines initial state for _requestingCaptionsModal based on AUTO_START_TRANSCRIBE.
 *
 * @returns {boolean}
 */
function _getInitialRequestingCaptionsModal() {
    try {
        if (typeof window !== 'undefined' && window.location) {
            const urlParams = new URLSearchParams(window.location.search);
            const autoStart = urlParams.get('AUTO_START_TRANSCRIBE') || urlParams.get('auto_start_transcribe');

            if (Number(autoStart) === 1) {
                return false;
            }
        }
        if (typeof config !== 'undefined' && Number(config?.AUTO_START_TRANSCRIBE) === 1) {
            return false;
        }
    } catch {
        // Ignore errors in non-browser environments
    }

    return true;
}

/**
 * Default State for 'features/transcription' feature.
 */
const defaultState = {
    _cachedTranscriptMessages: new Map(),
    _captionsView: jitsiLocalStorage.getItem('captionsView') === 'fixed' ? 'fixed' : 'float',
    _displaySubtitles: false,
    _transcriptMessages: new Map(),
    _requestingSubtitles: false,
    _language: null,
    _voiceCapabilities: new Map(),
    _voiceCatalogByParticipant: new Map(),
    _voiceModeByParticipant: new Map(),
    _translatedVoiceEnabled: false,
    _globalVoiceModeState: null,
    _translatedAudioTracksByParticipant: new Map(),
    _requestingCaptionsModal: _getInitialRequestingCaptionsModal(),
    _showDefaultTranscriptionLang: false,
    isLanguageSelectorModalOpen: false
};

/**
 * Listen for actions for the transcription feature to be used by the actions
 * to update the rendered transcription subtitles.
 */
ReducerRegistry.register('features/subtitles', (
        state = defaultState, action) => {
    switch (action.type) {
    case REMOVE_TRANSCRIPT_MESSAGE:
        return _removeTranscriptMessage(state, action);
    case REMOVE_CACHED_TRANSCRIPT_MESSAGE:
        return _removeCachedTranscriptMessage(state, action);
    case UPDATE_TRANSCRIPT_MESSAGE:
        return _updateTranscriptMessage(state, action);
    case SET_REQUESTING_SUBTITLES:
        return {
            ...state,
            _displaySubtitles: action.displaySubtitles,
            _language: action.language,
            _requestingSubtitles: action.enabled
        };
    case TOGGLE_REQUESTING_SUBTITLES:
        return {
            ...state,
            _requestingSubtitles: !state._requestingSubtitles
        };

    case TOGGLE_REQUESTING_CAPTIONS:
        return {
            ...state,
            _requestingCaptionsModal: typeof action.requestingCaptionsModal === 'boolean'
                ? action.requestingCaptionsModal
                : !state._requestingCaptionsModal
        };
    case _TRANSCRIBER_LEFT:
        return {
            ...state,
            ...defaultState,
            _captionsView: state._captionsView,
            _requestingCaptionsModal: _getInitialRequestingCaptionsModal()
        };
    case SET_CAPTIONS_VIEW:
        return {
            ...state,
            _captionsView: action.captionsView
        };
    case SET_DEFAULT_TRANSCRIPTION_LANGUAGE:
        return {
            ...state,
            _showDefaultTranscriptionLang: true
        };
    case OPEN_LANGUAGE_SELECTOR_MODAL:
        return {
            ...state,
            isLanguageSelectorModalOpen: true
        };
    case CLOSE_LANGUAGE_SELECTOR_MODAL:
        return {
            ...state,
            isLanguageSelectorModalOpen: false
        };
    case SET_VOICE_MODE_STATE:
        return _setVoiceModeState(state, action);
    case SET_TRANSLATED_VOICE_ENABLED:
        return {
            ...state,
            _translatedVoiceEnabled: action.enabled
        };
    case SET_VOICE_CAPABILITY:
        return _setVoiceCapability(state, action);
    case SET_VOICE_CATALOG:
        return _setVoiceCatalog(state, action);
    case SET_TRANSLATED_AUDIO_TRACK:
        return _setTranslatedAudioTrack(state, action);
    }


    return state;
});

/**
 * Reduces a specific Redux action REMOVE_TRANSCRIPT_MESSAGE of the feature
 * transcription.
 *
 * @param {Object} state - The Redux state of the feature transcription.
 * @param {Action} action -The Redux action REMOVE_TRANSCRIPT_MESSAGE to reduce.
 * @returns {Object} The new state of the feature transcription after the
 * reduction of the specified action.
 */
function _removeTranscriptMessage(state, { transcriptMessageID }) {
    const newTranscriptMessages = new Map(state._transcriptMessages);
    const message = newTranscriptMessages.get(transcriptMessageID);
    let { _cachedTranscriptMessages } = state;

    if (message && !message.final) {
        _cachedTranscriptMessages = new Map(_cachedTranscriptMessages);
        _cachedTranscriptMessages.set(transcriptMessageID, message);
    }

    // Deletes the key from Map once a final message arrives.
    newTranscriptMessages.delete(transcriptMessageID);

    return {
        ...state,
        _cachedTranscriptMessages,
        _transcriptMessages: newTranscriptMessages
    };
}

/**
 * Reduces a specific Redux action REMOVE_CACHED_TRANSCRIPT_MESSAGE of the feature transcription.
 *
 * @param {Object} state - The Redux state of the feature transcription.
 * @param {Action} action -The Redux action REMOVE_CACHED_TRANSCRIPT_MESSAGE to reduce.
 * @returns {Object} The new state of the feature transcription after the reduction of the specified action.
 */
function _removeCachedTranscriptMessage(state,
        { transcriptMessageID }) {

    const newCachedTranscriptMessages = new Map(state._cachedTranscriptMessages);

    // Deletes the key from Map once a final message arrives.
    newCachedTranscriptMessages.delete(transcriptMessageID);

    return {
        ...state,
        _cachedTranscriptMessages: newCachedTranscriptMessages
    };
}

/**
 * Reduces a specific Redux action UPDATE_TRANSCRIPT_MESSAGE of the feature
 * transcription.
 *
 * @param {Object} state - The Redux state of the feature transcription.
 * @param {Action} action -The Redux action UPDATE_TRANSCRIPT_MESSAGE to reduce.
 * @returns {Object} The new state of the feature transcription after the
 * reduction of the specified action.
 */
function _updateTranscriptMessage(state,
        { transcriptMessageID, newTranscriptMessage }) {
    const newTranscriptMessages = new Map(state._transcriptMessages);

    const _cachedTranscriptMessages = new Map(state._cachedTranscriptMessages);

    _cachedTranscriptMessages.delete(transcriptMessageID);

    // Updates the new message for the given key in the Map.
    newTranscriptMessages.set(transcriptMessageID, newTranscriptMessage);

    return {
        ...state,
        _cachedTranscriptMessages,
        _transcriptMessages: newTranscriptMessages
    };
}

/**
 * Stores voice mode state by participant and as latest global snapshot.
 *
 * @param {Object} state - Current subtitles redux state.
 * @param {Action} action - Redux action with payload.
 * @returns {Object}
 */
function _setVoiceModeState(state, { payload }) {
    if (!payload) {
        return state;
    }

    const participantID = payload.participant_id;
    const nextByParticipant = new Map(state._voiceModeByParticipant);

    if (participantID) {
        nextByParticipant.set(participantID, payload);
    }

    return {
        ...state,
        _globalVoiceModeState: payload,
        _voiceModeByParticipant: nextByParticipant
    };
}

/**
 * Stores clone/voice capability payload by participant.
 *
 * @param {Object} state - Current subtitles redux state.
 * @param {Action} action - Redux action with payload.
 * @returns {Object}
 */
function _setVoiceCapability(state, { payload }) {
    if (!payload || !payload.participant_id) {
        return state;
    }

    const nextCapabilities = new Map(state._voiceCapabilities);

    nextCapabilities.set(payload.participant_id, payload);

    return {
        ...state,
        _voiceCapabilities: nextCapabilities
    };
}

/**
 * Stores provider voice catalog payload by participant.
 *
 * @param {Object} state - Current subtitles redux state.
 * @param {Action} action - Redux action with payload.
 * @returns {Object}
 */
function _setVoiceCatalog(state, { payload }) {
    if (!payload || !payload.participant_id) {
        return state;
    }

    const nextCatalog = new Map(state._voiceCatalogByParticipant);

    nextCatalog.set(payload.participant_id, payload);

    return {
        ...state,
        _voiceCatalogByParticipant: nextCatalog
    };
}

/**
 * Stores a translated-audio-track announcement, keyed by speaker then by
 * target language, so a speaker can have multiple concurrently-tracked
 * languages (each independently active/inactive) rather than only the
 * latest announcement overwriting the rest.
 *
 * @param {Object} state - Current subtitles redux state.
 * @param {Action} action - Redux action with payload.
 * @returns {Object}
 */
function _setTranslatedAudioTrack(state, { payload }) {
    if (!payload || !payload.speaker_id || !payload.language) {
        return state;
    }

    const nextByParticipant = new Map(state._translatedAudioTracksByParticipant);
    const nextForSpeaker = new Map(nextByParticipant.get(payload.speaker_id));

    nextForSpeaker.set(payload.language, payload);
    nextByParticipant.set(payload.speaker_id, nextForSpeaker);

    return {
        ...state,
        _translatedAudioTracksByParticipant: nextByParticipant
    };
}
